'use client';

import { TextField } from '@petsmart-ui/sparky';
import { useField, useFormikContext } from 'formik';
import debounce from 'lodash.debounce';
import type { ChangeEvent, ReactElement } from 'react';
import { useCallback, useEffect } from 'react';

import { type EmailStatusResponse, getNextApiRoute, typedFetch } from '@libs/common-next';
import { HttpMethod } from '@libs/core';

import { emailExistsMsg } from './constants';
import type { EmailFieldProps, SignInFormValues } from './types';

export function EmailField({ isFormHidden, uniqueId, emailExists, setEmailExists }: EmailFieldProps): ReactElement {
    const { setFieldValue, setFieldTouched } = useFormikContext<SignInFormValues>();
    const [{ value, onChange, onBlur }, { touched, error }] = useField('email');

    useEffect(() => {
        if (isFormHidden) {
            setFieldValue('email', '');
            setFieldTouched('email', false);
        }
    }, [isFormHidden]);

    const checkEmailStatus = useCallback(
        async (email: string) => {
            if (email && !error && !emailExists) {
                const response: EmailStatusResponse | null = await typedFetch(
                    getNextApiRoute(`account/validations/accounts`),
                    {
                        method: HttpMethod.POST,
                        body: JSON.stringify({ email }),
                        headers: {
                            'content-type': 'application/json',
                        },
                    }
                );
                const isExistingEmail = !!(response?.success && response?.message !== 'Profile and identity exist');
                setEmailExists(isExistingEmail);
                setFieldValue('isExistingEmail', isExistingEmail);
            }
        },
        [error, emailExists, setFieldValue]
    );

    const debouncedCheckEmail = useCallback(
        debounce(async (email: string) => {
            await checkEmailStatus(email);
        }, 500),
        [checkEmailStatus]
    );

    return (
        <TextField
            data-testid="email"
            wrapperTestId={`${uniqueId}-field`}
            id={uniqueId}
            label="Email address"
            type="email"
            name="email"
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onChange(e);
                setEmailExists(false);
            }}
            onBlur={e => {
                onBlur(e);
                debouncedCheckEmail(e.target.value.trim());
            }}
            data-cs-mask
            isError={emailExists || (touched && !!error)}
            fieldNote={emailExists ? emailExistsMsg : touched && error?.[0]}
        />
    );
}
